import { useEffect, useState } from "react";
import { Plus, Pencil, Car, UserRound, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TONE = { active: "bg-[#1E3A2B] text-white", maintenance: "bg-[#FBF1EC] text-[#C86D51]", off: "bg-[#FBF1EC] text-[#C86D51]", retired: "bg-[#F5F0EB] text-[#5C6B63]", inactive: "bg-[#F5F0EB] text-[#5C6B63]" };

const CONFIG = {
  jeeps: {
    title: "Jeeps", icon: Car, statuses: ["active", "maintenance", "retired"],
    blank: { name: "", plate: "", seats: 6, status: "active", notes: "" },
    fields: [["name", "Name (e.g. Gypsy 07)"], ["plate", "Number plate"], ["seats", "Seats", "number"]],
    line: (j) => `${j.plate} · ${j.seats} seats`,
    toPayload: (f) => ({ ...f, seats: Number(f.seats) }),
  },
  drivers: {
    title: "Drivers", icon: UserRound, statuses: ["active", "off", "inactive"],
    blank: { name: "", phone: "", license_no: "", languages: ["Hindi"], status: "active", notes: "" },
    fields: [["name", "Full name"], ["phone", "Mobile / WhatsApp"], ["license_no", "Licence no."], ["languages", "Languages (comma separated)"]],
    line: (d) => `${d.phone}${d.license_no ? ` · DL ${d.license_no}` : ""}${d.languages?.length ? ` · ${d.languages.join(", ")}` : ""}`,
    toPayload: (f) => ({ ...f, languages: (Array.isArray(f.languages) ? f.languages : String(f.languages).split(",")).map((x) => x.trim()).filter(Boolean) }),
  },
  locations: {
    title: "Pickup & drop locations", icon: MapPin, statuses: ["active", "inactive"],
    blank: { name: "", area: "", kind: "both", pickup_offset_min: 30, pickup_charge: 0, drop_charge: 0, charge_basis: "booking", map_url: "", status: "active", notes: "" },
    fields: [["name", "Location name"], ["area", "Area / village"], ["pickup_offset_min", "Pickup minutes before departure", "number"],
      ["pickup_charge", "Extra price for PICKUP here (₹)", "number"], ["drop_charge", "Extra price for DROP here (₹)", "number"], ["map_url", "Google Maps link (optional)"]],
    selects: [["kind", "Use for", ["both", "pickup", "drop"]], ["charge_basis", "Charge", ["booking", "jeep"]]],
    line: (l) => `${l.area ? `${l.area} · ` : ""}${l.kind} · pickup ${l.pickup_offset_min} min before · pickup ${l.pickup_charge ? `+₹${l.pickup_charge}` : "free"} · drop ${l.drop_charge ? `+₹${l.drop_charge}` : "free"}${l.pickup_charge || l.drop_charge ? ` per ${l.charge_basis}` : ""}`,
    toPayload: (f) => ({ ...f, pickup_offset_min: Number(f.pickup_offset_min), pickup_charge: Number(f.pickup_charge), drop_charge: Number(f.drop_charge) }),
  },
};

export function FleetList({ kind }) {
  const c = CONFIG[kind];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get(`/admin/${kind}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const { id, created_at, ...rest } = c.toPayload(form);
      if (id) await api.patch(`/admin/${kind}/${id}`, rest); else await api.post(`/admin/${kind}`, rest);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-[#1E3A2B] flex items-center gap-2"><c.icon size={18}/>{c.title} <span className="text-sm text-[#5C6B63] font-sans">({rows.filter((r) => r.status === "active").length} active)</span></h3>
        <button onClick={() => setForm(c.blank)} className="jt-pill-accent text-sm py-2" data-testid={`fleet-new-${kind}`}><Plus size={14}/>Add</button>
      </div>
      <div className="bg-white rounded-xl ring-1 ring-black/5 divide-y">
        {rows.map((x) => (
          <div key={x.id} className="p-3 flex items-center gap-3" data-testid={`fleet-${kind}-row-${x.id}`}>
            <div className="flex-1">
              <div className="font-medium">{x.name}</div>
              <div className="text-xs text-[#5C6B63]">{c.line(x)}</div>
              {x.notes && <div className="text-xs text-[#8EA094] mt-0.5">{x.notes}</div>}
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs ${TONE[x.status]}`}>{x.status}</span>
            <button onClick={() => setForm({ ...x, languages: x.languages?.join(", ") })} className="p-2" data-testid={`fleet-edit-${x.id}`}><Pencil size={15}/></button>
          </div>
        ))}
        {rows.length === 0 && <div className="p-4 text-sm text-[#5C6B63]">None yet.</div>}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {form && (
          <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} {{ jeeps: "jeep", drivers: "driver", locations: "location" }[kind]}</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              {c.fields.map(([k, label, type]) => (
                <div key={k}><label className="jt-eyebrow mb-1 block">{label}</label>
                  <Input type={type || "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`fleet-f-${k}`}/></div>
              ))}
              {(c.selects || []).map(([k, label, opts]) => (
                <div key={k}><label className="jt-eyebrow mb-1 block">{label}</label>
                  <Select value={form[k]} onValueChange={(v) => setForm({ ...form, [k]: v })}>
                    <SelectTrigger data-testid={`fleet-f-${k}`}><SelectValue/></SelectTrigger>
                    <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              ))}
              <div><label className="jt-eyebrow mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="fleet-f-status"><SelectValue/></SelectTrigger>
                  <SelectContent>{c.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="jt-eyebrow mb-1 block">Notes</label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
            </div>
            <DialogFooter><button onClick={save} className="jt-pill-primary text-sm" data-testid="fleet-save">Save</button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

export default function FleetManager() {
  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-8">
      <FleetList kind="jeeps"/>
      <FleetList kind="drivers"/>
    </div>
  );
}
